import React from "react";
import WebProfitSpinner from "../../system/WebProfitSpinner";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { erpApi } from "../../../api/erp";
import { ERP_BRAND } from "../../../utils/erp";
import { resolveProductImageUrl } from "../../../utils/productMedia";

const FILTERS = [
  { value: "all", label: "Todos" },
  { value: "pending", label: "Pendientes" },
  { value: "counted", label: "Contados" },
];

function quantityText(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
}

function signed(value) {
  const number = Number(value || 0);
  if (!number) return "0";
  return number > 0 ? `+${number}` : String(number);
}

function SaveState({ pendingCount, errors }) {
  if (Object.keys(errors).length) return <Chip size="small" color="error" label="Cambios sin guardar" />;
  if (pendingCount > 0) return <Chip size="small" icon={<WebProfitSpinner size={13} />} label="Guardando…" />;
  return <Chip size="small" color="success" variant="outlined" label="Guardado" />;
}

function CountLineCard({ line, inputValue, onInputChange, onInputBlur, onIncrement, onDecrement, onPending, onNext, inputRef }) {
  const counted = line.countedQuantity !== null && line.countedQuantity !== undefined;
  const image = resolveProductImageUrl(line.imagePathSnapshot || "");
  const descriptor = [line.variantLabelSnapshot, line.variantSkuSnapshot || line.codeSnapshot].filter(Boolean).join(" · ");

  return (
    <Box
      sx={{
        border: `1px solid ${counted ? "rgba(39,174,96,.32)" : ERP_BRAND.border}`,
        bgcolor: counted ? "rgba(39,174,96,.035)" : "var(--wp-panel-surface, #fff)",
        borderRadius: 3,
        p: { xs: 1.25, sm: 1.6 },
      }}
    >
      <Stack spacing={1.15}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <Avatar
            variant="rounded"
            src={image || undefined}
            sx={{ width: 48, height: 48, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)", color: ERP_BRAND.muted, flexShrink: 0 }}
          >
            <InventoryRoundedIcon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, lineHeight: 1.2 }}>{line.productNameSnapshot}</Typography>
            {descriptor ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{descriptor}</Typography> : null}
            <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap" sx={{ mt: 0.45 }}>
              {line.categorySnapshot ? <Chip size="small" variant="outlined" label={line.categorySnapshot} /> : null}
              <Chip size="small" color={counted ? "success" : "default"} label={counted ? "Contado" : "Pendiente"} />
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="stretch">
          <IconButton
            aria-label={`Restar cantidad de ${line.productNameSnapshot}${line.variantLabelSnapshot ? ` ${line.variantLabelSnapshot}` : ""}`}
            onClick={onDecrement}
            sx={{ width: 48, minWidth: 48, border: `1px solid ${ERP_BRAND.border}`, borderRadius: 2.5 }}
          >
            <RemoveRoundedIcon />
          </IconButton>
          <TextField
            inputRef={inputRef}
            value={inputValue}
            onChange={(event) => onInputChange(event.target.value)}
            onBlur={onInputBlur}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onNext();
              }
            }}
            fullWidth
            type="number"
            label={line.unitSnapshot ? `Encontrado (${line.unitSnapshot})` : "Cantidad encontrada"}
            inputProps={{ min: 0, step: "any", inputMode: "decimal" }}
            sx={{
              "& .MuiInputBase-root": { minHeight: 48 },
              "& input": { textAlign: "center", fontSize: 20, fontWeight: 950 },
            }}
          />
          <IconButton
            aria-label={`Sumar cantidad de ${line.productNameSnapshot}${line.variantLabelSnapshot ? ` ${line.variantLabelSnapshot}` : ""}`}
            onClick={onIncrement}
            sx={{ width: 48, minWidth: 48, border: `1px solid ${ERP_BRAND.border}`, borderRadius: 2.5 }}
          >
            <AddRoundedIcon />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={0.8} justifyContent="space-between">
          <Button onClick={onPending} size="small" sx={{ textTransform: "none", fontWeight: 850 }} disabled={!counted && inputValue === ""}>
            No contado todavía
          </Button>
          <Button onClick={onNext} size="small" endIcon={<ArrowForwardRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>
            Siguiente
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function ReviewRow({ line, onOmit, disabled }) {
  return (
    <Box sx={{ border: `1px solid ${line.blocking ? "rgba(211,47,47,.35)" : ERP_BRAND.border}`, borderRadius: 3, p: 1.4, bgcolor: "var(--wp-panel-surface, #fff)" }}>
      <Stack spacing={0.7}>
        <Box>
          <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{line.productNameSnapshot}</Typography>
          {line.variantLabelSnapshot || line.variantSkuSnapshot ? (
            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
              {[line.variantLabelSnapshot, line.variantSkuSnapshot].filter(Boolean).join(" · ")}
            </Typography>
          ) : null}
        </Box>
        {line.counted ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.2, sm: 2 }}>
            <Typography variant="body2">Referencia al contar: <b>{line.stockReference}</b></Typography>
            <Typography variant="body2">Encontrado: <b>{line.countedQuantity}</b></Typography>
            <Typography variant="body2">Diferencia: <b>{signed(line.delta)}</b></Typography>
          </Stack>
        ) : <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>No contado todavía</Typography>}
        {line.movedAfterCount ? (
          <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
            Stock actual: <b>{line.currentStock}</b> · luego del ajuste quedaría en <b>{line.projectedStock}</b>.
          </Typography>
        ) : null}
        {line.warning ? <Alert severity={line.blocking ? "error" : "warning"} sx={{ py: 0 }}>{line.warning}</Alert> : null}
        {line.blocking ? (
          <Button onClick={onOmit} disabled={disabled} size="small" variant="outlined" sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>
            Omitir y dejar pendiente
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function InitialStockCountDialog({
  open,
  onClose,
  projectId,
  activeCount,
  canConfirm,
  onChanged,
  onGoItems,
  onGoMovements,
}) {
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const [mode, setMode] = React.useState("intro");
  const [session, setSession] = React.useState(null);
  const [summary, setSummary] = React.useState(null);
  const [lines, setLines] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("pending");
  const [page, setPage] = React.useState(1);
  const [totalFiltered, setTotalFiltered] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [quantityInputs, setQuantityInputs] = React.useState({});
  const [saveErrors, setSaveErrors] = React.useState({});
  const [pendingSaveCount, setPendingSaveCount] = React.useState(0);
  const [review, setReview] = React.useState(null);
  const [reviewScope, setReviewScope] = React.useState("differences");
  const [reviewPage, setReviewPage] = React.useState(1);
  const [confirming, setConfirming] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const queuesRef = React.useRef(new Map());
  const inputRefs = React.useRef(new Map());
  const focusFirstRef = React.useRef(false);
  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 280);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    const warn = (event) => {
      if (!open || (pendingSaveCount === 0 && !Object.keys(saveErrors).length)) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [open, pendingSaveCount, saveErrors]);

  const syncPendingSaveCount = React.useCallback(() => {
    setPendingSaveCount([...queuesRef.current.values()].filter((entry) => entry.inFlight || entry.dirtyValue !== undefined).length);
  }, []);

  const mergeLine = React.useCallback((serverLine, sentValue) => {
    setLines((current) => current.map((line) => {
      if (line.id !== serverLine.id) return line;
      const queue = queuesRef.current.get(line.id);
      const newerLocalValue = queue?.dirtyValue;
      return {
        ...line,
        ...serverLine,
        ...(newerLocalValue !== undefined && newerLocalValue !== sentValue ? { countedQuantity: newerLocalValue } : {}),
      };
    }));
  }, []);

  const flushLine = React.useCallback(async (lineId) => {
    const entry = queuesRef.current.get(lineId);
    if (!entry || entry.inFlight || entry.dirtyValue === undefined || !session?.id) return entry?.promise;
    if (entry.timer) window.clearTimeout(entry.timer);
    const sentValue = entry.dirtyValue;
    entry.dirtyValue = undefined;
    entry.inFlight = true;
    entry.error = null;
    syncPendingSaveCount();
    setSaveErrors((current) => {
      const next = { ...current };
      delete next[lineId];
      return next;
    });

    entry.promise = erpApi.updateStockCountLine(projectId, session.id, lineId, sentValue)
      .then((data) => {
        mergeLine(data.line, sentValue);
        setSummary(data.summary || null);
      })
      .catch((err) => {
        const message = err.message || "No se pudo guardar";
        entry.error = message;
        entry.dirtyValue = entry.dirtyValue === undefined ? sentValue : entry.dirtyValue;
        setSaveErrors((current) => ({ ...current, [lineId]: message }));
      })
      .finally(() => {
        entry.inFlight = false;
        syncPendingSaveCount();
        if (entry.dirtyValue !== undefined && !entry.error) {
          entry.timer = window.setTimeout(() => flushLine(lineId), 350);
        }
      });
    return entry.promise;
  }, [mergeLine, projectId, session?.id, syncPendingSaveCount]);

  const queueSave = React.useCallback((lineId, value) => {
    let entry = queuesRef.current.get(lineId);
    if (!entry) {
      entry = { dirtyValue: undefined, inFlight: false, timer: null, promise: null, error: null };
      queuesRef.current.set(lineId, entry);
    }
    entry.dirtyValue = value;
    entry.error = null;
    setSaveErrors((current) => {
      const next = { ...current };
      delete next[lineId];
      return next;
    });
    syncPendingSaveCount();
    if (entry.timer) window.clearTimeout(entry.timer);
    entry.timer = window.setTimeout(() => flushLine(lineId), 420);
  }, [flushLine, syncPendingSaveCount]);

  const flushAll = React.useCallback(async () => {
    const ids = [...queuesRef.current.keys()];
    for (const lineId of ids) {
      const entry = queuesRef.current.get(lineId);
      if (entry?.timer) window.clearTimeout(entry.timer);
      if (entry?.inFlight && entry.promise) await entry.promise;
      if (entry?.dirtyValue !== undefined) await flushLine(lineId);
      const latest = queuesRef.current.get(lineId);
      if (latest?.inFlight && latest.promise) await latest.promise;
    }
    syncPendingSaveCount();
    return [...queuesRef.current.values()].every((entry) => !entry.inFlight && entry.dirtyValue === undefined && !entry.error);
  }, [flushLine, syncPendingSaveCount]);

  const loadLines = React.useCallback(async (countId = session?.id) => {
    if (!countId) return;
    setLoading(true);
    setError("");
    try {
      const data = await erpApi.getStockCount(projectId, countId, {
        page,
        pageSize: 50,
        q: search,
        category,
        status: statusFilter,
      });
      setSession(data.session);
      setSummary(data.summary);
      setCategories(data.categories || []);
      setTotalFiltered(Number(data.totalFiltered || 0));
      setHasMore(Boolean(data.hasMore));
      setLines((data.lines || []).map((line) => {
        const queue = queuesRef.current.get(line.id);
        return queue?.dirtyValue !== undefined ? { ...line, countedQuantity: queue.dirtyValue } : line;
      }));
      setQuantityInputs((current) => {
        const next = { ...current };
        for (const line of data.lines || []) {
          const queue = queuesRef.current.get(line.id);
          if (next[line.id] === undefined || queue?.dirtyValue === undefined) next[line.id] = quantityText(line.countedQuantity);
        }
        return next;
      });
      if (data.session?.status === "confirming") {
        setMode("review");
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar el conteo");
    } finally {
      setLoading(false);
    }
  }, [category, page, projectId, search, session?.id, statusFilter]);

  const loadReview = React.useCallback(async (countId = session?.id) => {
    if (!countId) return;
    setLoading(true);
    setError("");
    try {
      const saved = await flushAll();
      if (!saved) {
        setError("Hay cambios que todavía no pudieron guardarse. Reintentá antes de revisar.");
        setMode("work");
        return;
      }
      const data = await erpApi.reviewStockCount(projectId, countId, {
        page: reviewPage,
        pageSize: 50,
        scope: reviewScope,
      });
      setSession(data.session);
      setSummary(data.summary);
      setReview(data);
    } catch (err) {
      setError(err.message || "No se pudo preparar la revisión");
    } finally {
      setLoading(false);
    }
  }, [flushAll, projectId, reviewPage, reviewScope, session?.id]);

  React.useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;
    setError("");
    setResult(null);
    setReview(null);
    queuesRef.current.clear();
    setSaveErrors({});
    setPendingSaveCount(0);
    if (activeCount?.session?.id) {
      setSession(activeCount.session);
      setSummary(activeCount.summary || null);
      setMode(activeCount.session.status === "confirming" ? "review" : "work");
      setPage(1);
    } else {
      setSession(null);
      setSummary(null);
      setMode("intro");
    }
  }, [activeCount, open]);

  React.useEffect(() => {
    if (!open || mode !== "work" || !session?.id) return;
    loadLines(session.id);
  }, [loadLines, mode, open, session?.id]);

  React.useEffect(() => {
    if (!open || mode !== "review" || !session?.id) return;
    loadReview(session.id);
  }, [loadReview, mode, open, session?.id]);

  React.useEffect(() => {
    if (!focusFirstRef.current || loading || !lines.length) return;
    focusFirstRef.current = false;
    const target = lines.find((line) => line.countedQuantity === null) || lines[0];
    window.setTimeout(() => inputRefs.current.get(target.id)?.focus?.(), 50);
  }, [lines, loading]);

  const start = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await erpApi.startStockCount(projectId);
      setSession(data.session);
      setSummary(data.summary);
      setMode("work");
      setStatusFilter("pending");
      setPage(1);
      onChanged?.();
    } catch (err) {
      setError(err.message || "No se pudo iniciar el conteo");
    } finally {
      setLoading(false);
    }
  };

  const setLineLocal = (lineId, value) => {
    setLines((current) => current.map((line) => line.id === lineId ? { ...line, countedQuantity: value } : line));
    setQuantityInputs((current) => ({ ...current, [lineId]: quantityText(value) }));
    queueSave(lineId, value);
  };

  const inputChange = (line, raw) => {
    setQuantityInputs((current) => ({ ...current, [line.id]: raw }));
    if (raw === "") return;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) return;
    setLines((current) => current.map((item) => item.id === line.id ? { ...item, countedQuantity: value } : item));
    queueSave(line.id, value);
  };

  const changeBy = (line, delta) => {
    const raw = quantityInputs[line.id];
    const current = raw !== "" && Number.isFinite(Number(raw))
      ? Number(raw)
      : line.countedQuantity === null ? 0 : Number(line.countedQuantity || 0);
    const next = Math.max(0, current + delta);
    setLineLocal(line.id, next);
  };

  const goNext = (index) => {
    const next = lines.slice(index + 1).find((line) => line.countedQuantity === null);
    if (next) {
      inputRefs.current.get(next.id)?.focus?.();
      inputRefs.current.get(next.id)?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      return;
    }
    if (hasMore) {
      focusFirstRef.current = true;
      setPage((value) => value + 1);
    }
  };

  const retrySaves = async () => {
    setSaveErrors({});
    for (const entry of queuesRef.current.values()) entry.error = null;
    await flushAll();
  };

  const refreshCatalog = async () => {
    if (!session?.id) return;
    setLoading(true);
    setError("");
    try {
      const data = await erpApi.refreshStockCountCatalog(projectId, session.id);
      setSummary(data.summary);
      setPage(1);
      await loadLines(session.id);
      if (data.added > 0) setError(`Se agregaron ${data.added} existencias nuevas como pendientes.`);
      onChanged?.();
    } catch (err) {
      setError(err.message || "No se pudo actualizar el catálogo del conteo");
    } finally {
      setLoading(false);
    }
  };

  const omitReviewLine = async (line) => {
    setLoading(true);
    setError("");
    try {
      await erpApi.updateStockCountLine(projectId, session.id, line.id, null);
      onChanged?.();
      await loadReview(session.id);
    } catch (err) {
      setError(err.message || "No se pudo dejar la línea pendiente");
    } finally {
      setLoading(false);
    }
  };

  const confirm = async (allowPending) => {
    if (!session?.id || !canConfirm) return;
    setConfirming(true);
    setError("");
    try {
      const data = await erpApi.confirmStockCount(projectId, session.id, { allowPending });
      setResult(data.result);
      setMode("result");
      onChanged?.({ completed: true });
    } catch (err) {
      setError(err.message || "No se pudo confirmar el conteo. Podés reintentar con la misma sesión.");
      await loadReview(session.id).catch(() => {});
    } finally {
      setConfirming(false);
    }
  };

  const discard = async () => {
    if (!session?.id || !window.confirm("¿Descartar este conteo? El stock real no se modificará.")) return;
    setLoading(true);
    setError("");
    for (const entry of queuesRef.current.values()) {
      if (entry.timer) window.clearTimeout(entry.timer);
      entry.timer = null;
      entry.dirtyValue = undefined;
    }
    syncPendingSaveCount();
    try {
      await erpApi.discardStockCount(projectId, session.id);
      onChanged?.({ discarded: true });
      onClose?.();
    } catch (err) {
      setError(err.message || "No se pudo descartar el conteo");
    } finally {
      setLoading(false);
    }
  };

  const safeClose = async () => {
    const unsaved = pendingSaveCount > 0 || Object.keys(saveErrors).length > 0 || [...queuesRef.current.values()].some((entry) => entry.dirtyValue !== undefined);
    if (unsaved && !window.confirm("Hay cambios que todavía no llegaron al servidor. ¿Salir igualmente?")) return;
    if (unsaved) {
      for (const entry of queuesRef.current.values()) {
        if (entry.timer) window.clearTimeout(entry.timer);
        entry.timer = null;
        entry.dirtyValue = undefined;
      }
      syncPendingSaveCount();
    }
    onChanged?.();
    onClose?.();
  };

  const title = mode === "intro" ? "Conteo inicial" : mode === "review" ? "Revisar conteo" : mode === "result" ? "Conteo confirmado" : "Conteo inicial";

  return (
    <Dialog open={open} onClose={safeClose} fullScreen={mobile} fullWidth maxWidth="md" PaperProps={{ sx: { maxHeight: mobile ? "100dvh" : "92dvh" } }}>
      <DialogTitle sx={{ py: 1.2, px: { xs: 1.25, sm: 2.5 }, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {mode === "review" && session?.status !== "confirming" ? (
            <IconButton aria-label="Volver al conteo" onClick={() => setMode("work")}><ArrowBackRoundedIcon /></IconButton>
          ) : null}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{title}</Typography>
            {mode === "work" && summary ? (
              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{summary.counted} / {summary.total} contados · {summary.pending} pendientes</Typography>
            ) : null}
          </Box>
          {mode === "work" ? <SaveState pendingCount={pendingSaveCount} errors={saveErrors} /> : null}
          <IconButton aria-label="Cerrar conteo" onClick={safeClose}><CloseRoundedIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.25, sm: 2.5 }, bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", overflowX: "hidden" }}>
        <Stack spacing={1.5}>
          {error ? (
            <Alert severity={error.startsWith("Se agregaron") ? "success" : "error"} onClose={() => setError("")}>
              {error}
              {Object.keys(saveErrors).length ? <Button size="small" onClick={retrySaves} sx={{ ml: 1 }}>Reintentar guardado</Button> : null}
            </Alert>
          ) : null}

          {mode === "intro" ? (
            <Stack spacing={2.2} sx={{ py: { xs: 2, sm: 4 }, maxWidth: 620, mx: "auto" }}>
              <Box sx={{ width: 62, height: 62, borderRadius: 4, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.1)", color: ERP_BRAND.orange }}>
                <InventoryRoundedIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Contá lo que encontrás</Typography>
                <Typography sx={{ color: ERP_BRAND.muted, mt: 0.8 }}>
                  WebProfit guarda el progreso, calcula las diferencias y no cambia tu stock hasta que confirmes al final.
                </Typography>
              </Box>
              <Alert severity="info">Cero significa “lo conté y no encontré unidades”. “No contado todavía” queda pendiente y no modifica nada.</Alert>
              <Button variant="contained" size="large" onClick={start} disabled={loading} startIcon={loading ? <WebProfitSpinner size={17} /> : undefined} sx={{ minHeight: 52, textTransform: "none", fontWeight: 950 }}>
                {loading ? "Preparando conteo…" : "Empezar a contar"}
              </Button>
              <Button variant="text" onClick={onGoItems} disabled={loading} sx={{ textTransform: "none", fontWeight: 850 }}>Ir a Ítems</Button>
            </Stack>
          ) : null}

          {mode === "work" ? (
            <>
              <Box sx={{ position: "sticky", top: -10, zIndex: 2, bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", pt: 0.5, pb: 0.8 }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                    <Chip label={`${summary?.counted || 0} / ${summary?.total || 0} contados`} color="primary" variant="outlined" />
                    <Chip label={`${summary?.pending || 0} pendientes`} />
                    <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={refreshCatalog} disabled={loading} sx={{ textTransform: "none", fontWeight: 850 }}>Actualizar ítems</Button>
                  </Stack>
                  <TextField
                    value={searchInput}
                    onChange={(event) => { setSearchInput(event.target.value); setPage(1); }}
                    placeholder="Buscar por nombre, código o SKU"
                    size="small"
                    fullWidth
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8}>
                    <TextField select size="small" label="Categoría" value={category} onChange={(event) => { setCategory(event.target.value); setPage(1); }} fullWidth>
                      <MenuItem value="">Todas</MenuItem>
                      {categories.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                    </TextField>
                    <Stack direction="row" spacing={0.5} sx={{ overflowX: "auto", pb: 0.2 }}>
                      {FILTERS.map((filter) => (
                        <Button
                          key={filter.value}
                          variant={statusFilter === filter.value ? "contained" : "outlined"}
                          onClick={() => { setStatusFilter(filter.value); setPage(1); }}
                          sx={{ minHeight: 40, whiteSpace: "nowrap", textTransform: "none", fontWeight: 900 }}
                        >
                          {filter.label}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </Box>

              {loading && !lines.length ? <Stack alignItems="center" sx={{ py: 5 }}><WebProfitSpinner /></Stack> : null}
              {!loading && !lines.length ? <Alert severity="info">No hay líneas para estos filtros.</Alert> : null}
              <Stack spacing={1}>
                {lines.map((line, index) => (
                  <CountLineCard
                    key={line.id}
                    line={line}
                    inputValue={quantityInputs[line.id] ?? quantityText(line.countedQuantity)}
                    inputRef={(node) => { if (node) inputRefs.current.set(line.id, node); else inputRefs.current.delete(line.id); }}
                    onInputChange={(raw) => inputChange(line, raw)}
                    onInputBlur={() => {
                      if ((quantityInputs[line.id] ?? "") === "") {
                        setQuantityInputs((current) => ({ ...current, [line.id]: quantityText(line.countedQuantity) }));
                      }
                    }}
                    onIncrement={() => changeBy(line, 1)}
                    onDecrement={() => changeBy(line, -1)}
                    onPending={() => setLineLocal(line.id, null)}
                    onNext={() => goNext(index)}
                  />
                ))}
              </Stack>
              {totalFiltered > 50 ? (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Button disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}>Anterior</Button>
                  <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Página {page} · {totalFiltered} resultados</Typography>
                  <Button disabled={!hasMore || loading} onClick={() => { focusFirstRef.current = true; setPage((value) => value + 1); }}>Siguiente</Button>
                </Stack>
              ) : null}
            </>
          ) : null}

          {mode === "review" ? (
            <>
              {session?.status === "confirming" ? (
                <Alert severity="warning">La confirmación anterior quedó en recuperación. Reintentá con esta misma sesión; WebProfit reutiliza la misma clave y no duplica ajustes.</Alert>
              ) : null}
              <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                <Chip label={`${review?.summary?.counted ?? summary?.counted ?? 0} contados`} color="primary" />
                <Chip label={`${review?.summary?.pending ?? summary?.pending ?? 0} pendientes`} />
                <Chip label={`${review?.summary?.increase || 0} suben`} color="success" variant="outlined" />
                <Chip label={`${review?.summary?.decrease || 0} bajan`} color="warning" variant="outlined" />
                <Chip label={`${review?.summary?.unchanged || 0} sin diferencia`} variant="outlined" />
                <Chip label={`${review?.summary?.zeros || 0} en cero`} variant="outlined" />
              </Stack>
              {(review?.summary?.movedAfterCount || 0) > 0 ? <Alert severity="info">Hubo movimientos posteriores en {review.summary.movedAfterCount} líneas. Se conservarán: se aplicará sólo la diferencia observada al contar.</Alert> : null}
              {(review?.summary?.blocking || 0) > 0 ? <Alert severity="error">Hay {review.summary.blocking} líneas que deben dejarse pendientes o volver a contarse antes de confirmar.</Alert> : null}
              <Stack direction="row" spacing={0.7}>
                <Button variant={reviewScope === "differences" ? "contained" : "outlined"} onClick={() => { setReviewScope("differences"); setReviewPage(1); }} sx={{ textTransform: "none" }}>Diferencias</Button>
                <Button variant={reviewScope === "all" ? "contained" : "outlined"} onClick={() => { setReviewScope("all"); setReviewPage(1); }} sx={{ textTransform: "none" }}>Ver todo</Button>
              </Stack>
              {loading && !review ? <Stack alignItems="center" sx={{ py: 4 }}><WebProfitSpinner /></Stack> : null}
              {review && !review.lines.length ? <Alert severity="success">El stock contado coincide; no hay diferencias para revisar.</Alert> : null}
              <Stack spacing={1}>
                {(review?.lines || []).map((line) => <ReviewRow key={line.id} line={line} onOmit={() => omitReviewLine(line)} disabled={loading || confirming} />)}
              </Stack>
              {review?.totalFiltered > review?.pageSize ? (
                <Stack direction="row" justifyContent="space-between">
                  <Button disabled={reviewPage <= 1 || loading} onClick={() => setReviewPage((value) => Math.max(1, value - 1))}>Anterior</Button>
                  <Typography variant="caption" sx={{ alignSelf: "center", color: ERP_BRAND.muted }}>Página {reviewPage}</Typography>
                  <Button disabled={!review?.hasMore || loading} onClick={() => setReviewPage((value) => value + 1)}>Siguiente</Button>
                </Stack>
              ) : null}
              {!canConfirm ? <Alert severity="info">El conteo está listo. Un usuario con permiso para confirmar movimientos debe aplicarlo.</Alert> : null}
              {canConfirm && (review?.summary?.pending || 0) > 0 ? (
                <Alert severity="warning">
                  Quedan pendientes. Lo recomendado es volver a contarlos. Si finalizás sólo lo contado, esos ítems conservarán su stock actual y no recibirán ajustes.
                </Alert>
              ) : null}
            </>
          ) : null}

          {mode === "result" && result ? (
            <Stack spacing={2} alignItems="center" textAlign="center" sx={{ py: { xs: 3, sm: 5 } }}>
              <CheckCircleRoundedIcon color="success" sx={{ fontSize: 62 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Conteo confirmado</Typography>
                <Typography sx={{ color: ERP_BRAND.muted, mt: 0.7 }}>
                  {result.noAdjustmentsNeeded ? "El stock ya coincidía; no fue necesario aplicar ajustes." : `Se aplicaron ${result.adjusted} correcciones de stock.`}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" justifyContent="center">
                <Chip label={`${result.unchanged || 0} sin cambios`} />
                {result.pendingOmitted ? <Chip label={`${result.pendingOmitted} pendientes omitidos`} /> : null}
                {result.movementNumber ? <Chip label={`Movimiento ${result.movementNumber}`} variant="outlined" /> : null}
              </Stack>
              <Button variant="outlined" onClick={onGoMovements} sx={{ textTransform: "none", fontWeight: 900 }}>Ver movimientos de stock</Button>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      {mode === "work" ? (
        <DialogActions sx={{ px: { xs: 1.25, sm: 2.5 }, py: 1.2, borderTop: `1px solid ${ERP_BRAND.border}`, justifyContent: "space-between" }}>
          <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={discard} disabled={loading || pendingSaveCount > 0} sx={{ textTransform: "none" }}>Descartar</Button>
          <Button variant="contained" onClick={async () => { const saved = await flushAll(); if (!saved) { setError("Hay cambios que todavía no pudieron guardarse. Reintentá antes de revisar."); return; } setReviewPage(1); setMode("review"); }} disabled={loading || Object.keys(saveErrors).length > 0} sx={{ minHeight: 44, textTransform: "none", fontWeight: 950 }}>Revisar conteo</Button>
        </DialogActions>
      ) : null}
      {mode === "review" ? (
        <DialogActions sx={{ px: { xs: 1.25, sm: 2.5 }, py: 1.2, borderTop: `1px solid ${ERP_BRAND.border}`, flexWrap: "wrap" }}>
          {session?.status !== "confirming" ? <Button onClick={() => setMode("work")} sx={{ mr: "auto", textTransform: "none" }}>Volver a pendientes</Button> : null}
          {canConfirm && (review?.summary?.blocking || 0) === 0 && (review?.summary?.pending || 0) > 0 ? (
            <Button variant="contained" onClick={() => confirm(true)} disabled={confirming || loading} sx={{ textTransform: "none", fontWeight: 950 }}>
              {confirming ? "Confirmando…" : session?.status === "confirming" ? "Reintentar confirmación" : "Finalizar solo lo contado"}
            </Button>
          ) : null}
          {canConfirm && (review?.summary?.blocking || 0) === 0 && (review?.summary?.pending || 0) === 0 ? (
            <Button variant="contained" onClick={() => confirm(false)} disabled={confirming || loading} sx={{ textTransform: "none", fontWeight: 950 }}>
              {confirming ? "Confirmando…" : session?.status === "confirming" ? "Reintentar confirmación" : "Confirmar conteo"}
            </Button>
          ) : null}
        </DialogActions>
      ) : null}
      {mode === "result" ? (
        <DialogActions sx={{ px: 2.5, py: 1.2, borderTop: `1px solid ${ERP_BRAND.border}` }}>
          <Button variant="contained" onClick={onClose} sx={{ textTransform: "none", fontWeight: 950 }}>Listo</Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}
